import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";
import { AJUDA_PROTESE } from "@/lib/ajuda/dialogos/protese";
import { TERMINAL_STATUSES, formatDate, orderServiceSummary } from "./prosthesis-meta";
import { DENTIST_STATUS_META, dentistStatusBucket } from "./dentist-status";
import { ProsthesisOrderDetailDialog } from "./ProsthesisOrderDetailDialog";
import type { ProsthesisOrderRow } from "./types";

type Filtro = "abertos" | "todos";

/** Os pedidos de um dentista, aberto pelo cartão da aba "Por dentista". `userId: null` são os
 *  pedidos antigos sem autor registrado. */
export function PedidosDoDentistaDialog({
  dentista,
  onClose,
}: {
  dentista: { userId: string | null; nome: string } | null;
  onClose: () => void;
}) {
  const [filtro, setFiltro] = useState<Filtro>("abertos");
  const [pedidoAberto, setPedidoAberto] = useState<string | null>(null);
  const query = trpc.prosthesis.list.useQuery(
    { dentistaId: dentista?.userId ?? null, take: 500 },
    { enabled: !!dentista },
  );

  const pedidos = useMemo(() => {
    const todos = (query.data?.orders ?? []) as ProsthesisOrderRow[];
    const abertos = todos.filter((p) => !TERMINAL_STATUSES.includes(p.status));
    return { todos, abertos, mostrados: filtro === "abertos" ? abertos : todos };
  }, [query.data, filtro]);

  const filtros: [Filtro, string][] = [
    ["abertos", `Em aberto (${pedidos.abertos.length})`],
    ["todos", `Todos (${pedidos.todos.length})`],
  ];

  return (
    <>
      <Dialog open={!!dentista} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
          <DialogHeader>
            <DialogTitle ajuda={AJUDA_PROTESE.pedidosDoDentista}>{dentista?.nome}</DialogTitle>
            <DialogDescription>Pedidos de prótese em que é o dentista responsável.</DialogDescription>
          </DialogHeader>

          <div className="flex gap-1" role="group" aria-label="Filtro">
            {filtros.map(([chave, rotulo]) => (
              <button
                key={chave}
                type="button"
                onClick={() => setFiltro(chave)}
                aria-pressed={filtro === chave}
                className={cn(
                  "inline-flex h-8 items-center rounded-md px-2.5 text-xs font-medium transition-colors",
                  filtro === chave
                    ? "bg-brand-600 font-semibold text-white"
                    : "text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted",
                )}
              >
                {rotulo}
              </button>
            ))}
          </div>

          {query.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
            </div>
          ) : pedidos.mostrados.length === 0 ? (
            <p className="py-8 text-center text-sm text-zinc-500 dark:text-muted-foreground">
              {filtro === "abertos" ? "Nenhum pedido em aberto." : "Nenhum pedido."}
            </p>
          ) : (
            <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200 dark:divide-border dark:border-border">
              {pedidos.mostrados.map((p) => {
                const meta = DENTIST_STATUS_META[dentistStatusBucket(p.status)];
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => setPedidoAberto(p.id)}
                      className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-muted/40"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-zinc-900 dark:text-foreground">{p.patientName}</p>
                        <p className="truncate text-xs text-zinc-500 dark:text-muted-foreground">
                          {orderServiceSummary(p.services)} · {p.labName || "Sem laboratório"} · Prazo {formatDate(p.deadline)}
                        </p>
                      </div>
                      <span
                        className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase"
                        style={{ backgroundColor: meta.bg, color: meta.color }}
                      >
                        {meta.label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      <ProsthesisOrderDetailDialog orderId={pedidoAberto} onClose={() => setPedidoAberto(null)} />
    </>
  );
}
